<template>
  <div ref="pageRef" class="ccs-report">
    <!-- 顶部标题栏 -->
    <div ref="titleRef" class="page-header">
      <i class="iconfont icon-arrow_a" @click="router.back()"></i>
      <h1>CCS 报文发送</h1>
    </div>

    <!-- 基础信息栏 -->
    <div ref="infoBarRef" class="info-bar anim-card">
      <div class="info-item">
        <span class="info-label">版本号</span>
        <span class="info-value locked">2025</span>
      </div>
      <div class="info-divider"></div>
      <div class="info-item">
        <span class="info-label">报送方</span>
        <span class="info-value locked">锐深科技</span>
      </div>
    </div>

    <!-- 步骤流程 -->
    <div ref="stepperRef" class="stepper">
      <div class="step-line" :class="{ active: staticUploaded }"></div>
      <div class="step-line" :class="{ active: parseSuccess }"></div>
      <div
        class="step-node"
        :class="{ done: staticUploaded, current: !staticUploaded }"
      >
        <div class="step-circle">1</div>
        <span class="step-label">静态协议</span>
      </div>
      <div
        class="step-node"
        :class="{
          done: parseSuccess,
          current: staticUploaded && !parseSuccess,
        }"
      >
        <div class="step-circle">2</div>
        <span class="step-label">动态协议</span>
      </div>
      <div
        class="step-node"
        :class="{
          done: sendSuccess,
          current: parseSuccess && !sendSuccess,
        }"
      >
        <div class="step-circle">3</div>
        <span class="step-label">发送报文</span>
      </div>
    </div>

    <!-- Step 1: 静态协议 -->
    <div ref="staticStepRef" class="step-card anim-card">
      <div class="step-card-header">
        <div class="step-card-left">
          <span
            class="step-status-dot"
            :class="staticUploaded ? 'dot-success' : 'dot-pending'"
          ></span>
          <span class="step-card-title">Step 1</span>
          <span class="step-card-desc">上传静态协议 xlsx 文件</span>
        </div>
        <div class="step-card-right">
          <span v-if="staticFile" class="file-tag">{{ staticFile.name }}</span>
          <span
            class="status-tag"
            :class="staticUploaded ? 'tag-success' : 'tag-idle'"
            >{{ staticUploaded ? "已上传" : "未上传" }}</span
          >
        </div>
      </div>

      <el-upload
        class="ccs-upload"
        drag
        accept=".xls,.xlsx"
        :on-change="(file: any) => handleStaticFileChange(file)"
        :on-remove="handleStaticFileRemove"
        :auto-upload="false"
        :limit="1"
      >
        <div class="upload-area">
          <div class="upload-icon-wrap">
            <el-icon :size="32"><upload-filled /></el-icon>
          </div>
          <p class="upload-text">拖拽或点击上传静态协议文件</p>
          <p class="upload-hint">.xlsx / .xls</p>
        </div>
      </el-upload>

      <div class="step-actions">
        <el-button
          v-if="staticFile"
          type="primary"
          :loading="staticUploading"
          @click="uploadStaticFile"
        >
          上传文件
        </el-button>
        <el-button
          type="primary"
          plain
          :disabled="!staticUploaded || !staticParsedData"
          @click="toggleStaticPreview"
        >
          {{ staticPreviewVisible ? "收起解析结果" : "查看解析结果" }}
        </el-button>
      </div>

      <!-- 静态协议 JSON 预览 -->
      <transition name="preview-expand">
        <div
          v-if="staticParsedData && staticPreviewVisible"
          ref="staticPreviewRef"
          class="json-preview-wrap"
        >
          <div class="json-editor">
            <div class="json-toolbar">
              <span class="json-dot red"></span>
              <span class="json-dot yellow"></span>
              <span class="json-dot green"></span>
              <span class="json-filename">static-protocol.json</span>
              <span class="json-meta"
                >{{ jsonLineCount(staticParsedData) }} 行</span
              >
              <el-button
                class="copy-btn"
                size="small"
                text
                @click="copyJson(staticParsedData)"
              >
                复制
              </el-button>
            </div>
            <div class="json-body">
              <div class="json-line-numbers"></div>
              <pre
                class="json-code"
                v-html="highlightJson(staticParsedData)"
              ></pre>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Step 2: 准备动态协议（导出 xlsx / 在线编辑） -->
    <div
      ref="templateStepRef"
      class="step-card anim-card"
      :class="{ disabled: !staticUploaded }"
    >
      <div class="step-card-header">
        <div class="step-card-left">
          <span
            class="step-status-dot"
            :class="
              dynamicParsedReady
                ? 'dot-success'
                : staticUploaded
                ? 'dot-pending'
                : 'dot-disabled'
            "
          ></span>
          <span class="step-card-title">Step 2</span>
          <span class="step-card-desc">准备动态协议数据</span>
          <el-tag
            v-if="!staticUploaded"
            size="small"
            type="info"
            class="lock-tag"
          >
            请先完成 Step 1
          </el-tag>
        </div>
        <div class="step-card-right">
          <span
            class="status-tag"
            :class="dynamicParsedReady ? 'tag-success' : 'tag-idle'"
          >
            {{ step2StatusLabel }}
          </span>
        </div>
      </div>

      <el-tabs
        v-model="templateTab"
        class="template-tabs fancy-tabs"
        type="card"
        @tab-change="onTemplateTabChange"
      >
        <!-- Tab 1：导出 xlsx -->
        <el-tab-pane name="export">
          <template #label>
            <span class="fancy-tab-label">
              <el-icon><Download /></el-icon>
              <span>导出 xlsx</span>
            </span>
          </template>

          <div ref="exportPaneRef" class="tab-pane-inner">
            <div class="template-tip">
              <p>
                可以下载 CCS 动态协议模板 xlsx
                文件，按表格规则填写后，再上传到服务器。
                也可以直接上传你之前已经填好的 xlsx 文件，无需重新导出。
              </p>
              <p class="template-tip-small">
                模板包含 13 个 sheet：Ship / PowerSys / Charger / Compartment /
                BatteryGroup / BatteryCluster / BatteryPack / CDSys / CBus /
                Converter / Motor / Propeller / Rudder
              </p>
            </div>

            <div class="step-actions export-actions">
              <el-button
                type="warning"
                :disabled="!staticUploaded"
                :loading="templateDownloading"
                @click="downloadDynamicTemplate"
              >
                <el-icon style="margin-right: 4px"><Download /></el-icon>
                导出动态协议模板
              </el-button>
            </div>

            <el-upload
              class="ccs-upload"
              drag
              accept=".xls,.xlsx"
              :disabled="!staticUploaded"
              :on-change="(file: any) => handleDynamicFileChange(file)"
              :on-remove="handleDynamicFileRemove"
              :auto-upload="false"
              :limit="1"
            >
              <div class="upload-area">
                <div class="upload-icon-wrap">
                  <el-icon :size="32"><upload-filled /></el-icon>
                </div>
                <p class="upload-text">
                  {{
                    staticUploaded
                      ? "拖拽或点击上传动态协议文件"
                      : "请先完成 Step 1"
                  }}
                </p>
                <p class="upload-hint">.xlsx / .xls</p>
              </div>
            </el-upload>

            <div class="step-actions">
              <el-button
                type="primary"
                :disabled="!staticUploaded || !dynamicFile"
                :loading="dynamicUploading"
                @click="uploadDynamicFile"
              >
                上传文件
              </el-button>
              <span v-if="dynamicFilePath" class="upload-success-hint">
                <el-icon color="#67c23a"><Check /></el-icon>
                已上传：{{ dynamicFile?.name }}
              </span>
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 2：在线编辑 -->
        <el-tab-pane name="edit">
          <template #label>
            <span class="fancy-tab-label">
              <el-icon><EditPen /></el-icon>
              <span>在线编辑</span>
            </span>
          </template>

          <div ref="editPaneRef" class="tab-pane-inner">
            <div v-if="templateLoading" class="online-editor-loading">
              <el-icon class="is-loading"><Loading /></el-icon>
              <span>正在加载模板规则...</span>
            </div>

            <div v-else-if="templateSheets.length" class="online-editor">
              <!-- 左侧 Sheet 列表 -->
              <div ref="sheetListRef" class="sheet-list">
                <div
                  class="sheet-list-indicator"
                  :style="sheetIndicatorStyle"
                ></div>
                <div
                  v-for="(sheet, idx) in templateSheets"
                  :key="sheet.name"
                  :ref="(el) => setSheetItemRef(el, idx)"
                  class="sheet-item"
                  :class="{ active: idx === activeSheetIdx }"
                  @click="switchSheet(idx)"
                >
                  <span class="sheet-index">{{
                    String(idx + 1).padStart(2, "0")
                  }}</span>
                  <span class="sheet-name">{{ sheet.name }}</span>
                  <span class="sheet-rows">{{ sheet.rows.length }} 项</span>
                </div>
              </div>

              <!-- 右侧表格 -->
              <div class="sheet-panel">
                <div class="sheet-panel-header">
                  <div class="sheet-panel-title">
                    <span class="sheet-panel-name">{{
                      activeSheet?.name
                    }}</span>
                    <el-tag size="small" type="info" effect="plain">
                      {{
                        activeSheet?.codes
                          ? `数组型 · ${activeSheet?.codes?.length} 组`
                          : "对象型"
                      }}
                    </el-tag>
                    <el-tag
                      v-if="
                        activeSheet?.sourceColumns &&
                        activeSheet.sourceColumns > 1
                      "
                      size="small"
                      type="warning"
                      effect="plain"
                    >
                      值来源 {{ activeSheet?.sourceColumns }} 列
                    </el-tag>
                  </div>
                  <div class="sheet-panel-tip">
                    仅
                    <span class="tip-key">值来源</span>
                    列可编辑，其他列已锁定
                  </div>
                </div>

                <div ref="sheetTableRef" class="sheet-table-wrap">
                  <table class="sheet-table">
                    <thead>
                      <tr>
                        <th class="col-key">字段标识</th>
                        <th class="col-name">字段名称</th>
                        <th class="col-type">数据类型</th>
                        <th
                          v-if="(activeSheet?.sourceColumns ?? 1) > 1"
                          :colspan="activeSheet?.sourceColumns"
                          class="col-source-merged"
                        >
                          值来源
                        </th>
                        <th v-else class="col-source">值来源</th>
                      </tr>
                      <tr v-if="activeSheet?.codes" class="code-row">
                        <td class="locked">Code</td>
                        <td class="locked">编号</td>
                        <td class="locked">
                          <el-tag size="small" effect="dark">S</el-tag>
                        </td>
                        <td
                          v-for="(code, ci) in activeSheet.codes"
                          :key="ci"
                          class="code-cell"
                          :style="cellWidthStyle(ci)"
                        >
                          {{ code }}
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, ri) in activeSheet?.rows ?? []"
                        :key="ri"
                        class="data-row"
                      >
                        <td class="locked">
                          <span class="row-key">{{ row.key }}</span>
                        </td>
                        <td class="locked">{{ row.name }}</td>
                        <td class="locked">
                          <el-tag
                            size="small"
                            :type="typeTagType(row.type)"
                            effect="plain"
                          >
                            {{ row.type }}
                          </el-tag>
                        </td>
                        <td
                          v-for="(_, ci) in activeSheet?.sourceColumns ?? 1"
                          :key="ci"
                          class="editable-cell"
                          :style="cellWidthStyle(ci)"
                        >
                          <el-input
                            v-model="row.sources[ci]"
                            size="small"
                            placeholder="值来源"
                            class="cell-input"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="step-actions edit-actions">
              <el-button
                plain
                :disabled="!staticUploaded || templateLoading"
                @click="resetCurrentSheet"
              >
                重置本页
              </el-button>
              <el-button
                plain
                type="danger"
                :disabled="!staticUploaded || templateLoading"
                @click="resetAllSheets"
              >
                重置全部
              </el-button>
              <el-button
                type="success"
                :disabled="!staticUploaded || templateLoading"
                @click="confirmOnlineEdit"
              >
                确定
              </el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <!-- Step2 底部统一操作栏：解析协议 + 查看解析结果 -->
      <div class="parse-bar">
        <div class="parse-bar-status">
          <template v-if="parseLoading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>正在解析协议数据...</span>
          </template>
          <template v-else-if="parseSuccess">
            <el-icon color="#67c23a"><CircleCheck /></el-icon>
            <span class="parse-ok">协议解析成功，可发送报文</span>
          </template>
          <template v-else-if="canParse">
            <el-icon color="#e6a23c"><Warning /></el-icon>
            <span>{{
              templateTab === "export"
                ? "xlsx 已上传，请点击「解析协议」"
                : "在线规则已确定，请点击「解析协议」"
            }}</span>
          </template>
          <template v-else>
            <el-icon color="#909399"><InfoFilled /></el-icon>
            <span>{{
              templateTab === "export"
                ? "请先上传动态协议 xlsx 文件"
                : "请先编辑并点击「确定」"
            }}</span>
          </template>
        </div>
        <div class="parse-bar-actions">
          <el-button
            :type="parseSuccess ? 'success' : 'primary'"
            :disabled="!canParse || parseLoading || parseSuccess"
            :loading="parseLoading"
            @click="parseProtocol"
          >
            <el-icon v-if="parseSuccess" style="margin-right: 4px">
              <CircleCheck />
            </el-icon>
            {{ parseSuccess ? "已解析" : "解析协议" }}
          </el-button>
          <el-button
            type="primary"
            plain
            :disabled="!parseSuccess"
            @click="toggleDynamicPreview"
          >
            {{ dynamicPreviewVisible ? "收起解析结果" : "查看解析结果" }}
          </el-button>
        </div>
      </div>

      <!-- 解析结果 JSON 预览 -->
      <transition name="preview-expand">
        <div
          v-if="dynamicParsedData && dynamicPreviewVisible"
          ref="dynamicPreviewRef"
          class="json-preview-wrap"
        >
          <div class="json-editor">
            <div class="json-toolbar">
              <span class="json-dot red"></span>
              <span class="json-dot yellow"></span>
              <span class="json-dot green"></span>
              <span class="json-filename">dynamic-protocol.json</span>
              <span class="json-meta">
                {{ jsonLineCount(dynamicParsedData) }} 行
              </span>
              <el-button
                class="copy-btn"
                size="small"
                text
                @click="copyJson(dynamicParsedData)"
              >
                复制
              </el-button>
            </div>
            <div class="json-body">
              <div class="json-line-numbers"></div>
              <pre
                class="json-code"
                v-html="highlightJson(dynamicParsedData)"
              ></pre>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Step 3: 发送报文（只剩一个按钮） -->
    <div
      ref="dynamicStepRef"
      class="step-card anim-card"
      :class="{ disabled: !parseSuccess }"
    >
      <div class="step-card-header">
        <div class="step-card-left">
          <span
            class="step-status-dot"
            :class="
              sendSuccess
                ? 'dot-success'
                : parseSuccess
                ? 'dot-pending'
                : 'dot-disabled'
            "
          ></span>
          <span class="step-card-title">Step 3</span>
          <span class="step-card-desc">发送 CCS 报文</span>
          <el-tag
            v-if="!parseSuccess"
            size="small"
            type="info"
            class="lock-tag"
          >
            请先完成 Step 2
          </el-tag>
        </div>
        <div class="step-card-right">
          <span
            class="status-tag"
            :class="sendSuccess ? 'tag-success' : 'tag-idle'"
          >
            {{ sendSuccess ? "已发送" : "未发送" }}
          </span>
        </div>
      </div>

      <div class="send-step-body">
        <p class="send-step-hint">
          确认协议数据无误后，点击下方按钮将报文发送给 CCS 服务端。
        </p>
        <el-button
          ref="sendBtnRef"
          type="success"
          size="large"
          :disabled="!parseSuccess || sending"
          :loading="sending"
          @click="sendReport"
        >
          发送报文
        </el-button>
      </div>
    </div>

    <!-- 发送等待对话框 -->
    <el-dialog
      v-model="isWaitingVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      width="400px"
      center
    >
      <div class="waiting-content">
        <div class="waiting-ring"></div>
        <p class="waiting-title">正在发送报文...</p>
        <p class="waiting-subtitle">请稍候</p>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import {
  UploadFilled,
  Loading,
  Download,
  EditPen,
  Check,
  CircleCheck,
  Warning,
  InfoFilled,
} from "@element-plus/icons-vue";
import type { UploadFile } from "element-plus";
import {
  uploadCcsFileApi,
  sendCcsReportApi,
  uploadCcsFileRawApi,
  parseCcsFileApi,
  parseDynamicOnlineApi,
  downloadDynamicTemplateApi,
  fetchDynamicTemplateJsonApi,
  type TemplateSheet,
} from "@/api/operation/ccsReportApi";
import gsap from "gsap";

const router = useRouter();

// ======================== GSAP Refs ========================
const pageRef = ref<HTMLElement>();
const titleRef = ref<HTMLElement>();
const infoBarRef = ref<HTMLElement>();
const stepperRef = ref<HTMLElement>();
const staticStepRef = ref<HTMLElement>();
const templateStepRef = ref<HTMLElement>();
const dynamicStepRef = ref<HTMLElement>();

// ======================== 固定参数 ========================
const VERSION = 2025;
const AUTHOR = "锐深科技";

// ======================== 静态协议 ========================
const staticFile = ref<File | null>(null);
const staticFilePath = ref("");
const staticUploaded = ref(false);
const staticUploading = ref(false);
const staticParsing = ref(false);
const staticParsedData = ref<Record<string, any> | null>(null);
const staticPreviewVisible = ref(false);
const staticPreviewRef = ref<HTMLElement>();

/** 从静态协议解析结果中提取的 shipId，用于动态协议解析 */
const extractedShipId = ref("");

const handleStaticFileChange = (file: UploadFile) => {
  if (file && file.raw) {
    staticFile.value = file.raw;
    staticFilePath.value = "";
    staticUploaded.value = false;
    staticParsedData.value = null;
    extractedShipId.value = "";
  }
};
const handleStaticFileRemove = () => {
  staticFile.value = null;
  staticFilePath.value = "";
  staticUploaded.value = false;
  staticParsedData.value = null;
  extractedShipId.value = "";
};

const uploadStaticFile = async () => {
  if (!staticFile.value) return;
  staticUploading.value = true;
  try {
    const result = await uploadCcsFileApi(staticFile.value, "static", {
      version: VERSION,
      author: AUTHOR,
    });
    if (result) {
      staticFilePath.value = result.filePath;
      staticParsedData.value = result.parsedData;
      staticUploaded.value = true;
      const shipIdFromHeader =
        result.parsedData?.Package?.Header?.ShipID ||
        result.parsedData?.package?.Header?.ShipID ||
        "";
      extractedShipId.value = String(shipIdFromHeader);
      ElMessage.success("静态协议上传并解析成功");
      await nextTick();
      animateStepDone(staticStepRef.value);
    } else {
      ElMessage.error("文件上传或解析失败");
    }
  } finally {
    staticUploading.value = false;
  }
};

const toggleStaticPreview = async () => {
  staticPreviewVisible.value = !staticPreviewVisible.value;
  await nextTick();
  if (staticPreviewVisible.value) {
    animatePreview(staticPreviewRef.value);
    updateLineNumbers(staticPreviewRef.value);
  }
};

// ======================== 动态协议 ========================
const dynamicFile = ref<File | null>(null);
const dynamicFilePath = ref("");
const dynamicUploaded = ref(false);
const dynamicUploading = ref(false);
const dynamicParsing = ref(false);
const dynamicParsedData = ref<Record<string, any> | null>(null);
const templateDownloading = ref(false);
// 在线编辑相关
const templateTab = ref<"export" | "edit">("export");
const templateLoading = ref(false);
const templateSheets = ref<TemplateSheet[]>([]);
// 缓存原始模板（深拷贝），用于「重置本页 / 重置全部」时不再请求后端
const templateSheetsCache = ref<TemplateSheet[]>([]);
const onlineEditConfirmed = ref(false); // 在线编辑「确定」后置位
const activeSheetIdx = ref(0);
const sheetItemEls = ref<HTMLElement[]>([]);
const sheetListRef = ref<HTMLElement>();
const sheetTableRef = ref<HTMLElement>();
const exportPaneRef = ref<HTMLElement>();
const editPaneRef = ref<HTMLElement>();

// 解析协议相关
const parseLoading = ref(false);
const parseSuccess = ref(false);
// 发送结果
const sendSuccess = ref(false);

// 可以解析的条件：xlsx tab 下文件已上传，或 edit tab 下规则已确定
const canParse = computed(() => {
  if (templateTab.value === "export") {
    return !!dynamicFilePath.value;
  }
  return onlineEditConfirmed.value;
});

// Step2 总体是否已就绪（解析成功）
const dynamicParsedReady = computed(() => parseSuccess.value);

const step2StatusLabel = computed(() => {
  if (parseSuccess.value) return "解析成功";
  if (parseLoading.value) return "解析中";
  if (canParse.value) return "待解析";
  return "未准备";
});

const activeSheet = computed<TemplateSheet | null>(
  () => templateSheets.value[activeSheetIdx.value] ?? null
);

// Sheet 列表左侧 active 指示条样式（GSAP 接管）
const sheetIndicatorStyle = ref<Record<string, string>>({
  transform: "translateY(0px)",
  height: "0px",
  opacity: "0",
});

// 计算每列输入框最小宽度（取该列所有 source/code 文本最大字符数）
const cellWidthStyle = (colIdx: number) => {
  const sheet = activeSheet.value;
  if (!sheet) return {};
  let maxLen = 8;
  if (sheet.codes && sheet.codes[colIdx]) {
    maxLen = Math.max(maxLen, sheet.codes[colIdx].length);
  }
  for (const row of sheet.rows) {
    const v = row.sources[colIdx];
    if (v && v.length > maxLen) maxLen = v.length;
  }
  // 8px / 字符 + 24px padding
  const w = Math.min(Math.max(maxLen * 9 + 24, 110), 360);
  return { minWidth: `${w}px` };
};

const dynamicPreviewVisible = ref(false);
const dynamicPreviewRef = ref<HTMLElement>();

const handleDynamicFileChange = (file: UploadFile) => {
  if (file && file.raw) {
    dynamicFile.value = file.raw;
    dynamicFilePath.value = "";
    dynamicUploaded.value = false;
    dynamicParsedData.value = null;
  }
};
const handleDynamicFileRemove = () => {
  dynamicFile.value = null;
  dynamicFilePath.value = "";
  dynamicUploaded.value = false;
  dynamicParsedData.value = null;
};

const uploadDynamicFile = async () => {
  if (!dynamicFile.value) return;
  dynamicUploading.value = true;
  try {
    const filePath = await uploadCcsFileRawApi(dynamicFile.value, "dynamic");
    if (filePath) {
      dynamicFilePath.value = filePath;
      dynamicUploaded.value = true;
      // 一旦重新上传，需要重新解析
      parseSuccess.value = false;
      dynamicParsedData.value = null;
      dynamicPreviewVisible.value = false;
      ElMessage.success("动态协议文件上传成功，可点击「解析协议」");
    } else {
      ElMessage.error("文件上传失败");
    }
  } finally {
    dynamicUploading.value = false;
  }
};

// 下载动态协议模板 xlsx（导出按钮）
const downloadDynamicTemplate = async () => {
  if (!staticUploaded.value) {
    ElMessage.warning("请先上传静态协议");
    return;
  }
  templateDownloading.value = true;
  try {
    const blob = await downloadDynamicTemplateApi(
      staticParsedData.value ?? undefined
    );
    if (!blob) {
      ElMessage.error("模板下载失败");
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "CCS动态协议模板.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    ElMessage.success("模板已下载");
  } catch (e) {
    ElMessage.error("模板下载失败");
  } finally {
    templateDownloading.value = false;
  }
};

// ======================== Step2 在线编辑 ========================
const setSheetItemRef = (el: any, idx: number) => {
  if (el) sheetItemEls.value[idx] = el as HTMLElement;
};

// 数据类型 → Tag 颜色
const typeTagType = (
  type: string
): "primary" | "success" | "warning" | "info" | "danger" => {
  if (!type) return "info";
  if (type.startsWith("FA") || type.startsWith("IA")) return "warning";
  if (type.startsWith("F") || type.startsWith("D")) return "success";
  if (type === "I") return "primary";
  if (type === "S") return "info";
  return "info";
};

// Tab 切换：触发对应 Pane 的入场动画；首次进入"在线编辑"时拉取模板
const onTemplateTabChange = async (tabName: string | number) => {
  await nextTick();
  if (tabName === "edit") {
    if (!templateSheets.value.length) await loadTemplateJson();
    animatePane(editPaneRef.value, "right");
    await nextTick();
    moveSheetIndicator(false);
    animateSheetRowsIn();
  } else {
    animatePane(exportPaneRef.value, "left");
  }
};

// 拉取模板 JSON 并同时缓存原始数据（深拷贝）
const loadTemplateJson = async () => {
  templateLoading.value = true;
  try {
    const data = await fetchDynamicTemplateJsonApi(
      staticParsedData.value ?? undefined
    );
    if (data) {
      templateSheets.value = JSON.parse(JSON.stringify(data));
      templateSheetsCache.value = JSON.parse(JSON.stringify(data));
      activeSheetIdx.value = 0;
    } else {
      ElMessage.error("模板加载失败");
    }
  } finally {
    templateLoading.value = false;
  }
};

// 重置本页（不请求后端，从缓存恢复当前 sheet）
const resetCurrentSheet = () => {
  if (!templateSheetsCache.value.length) return;
  const idx = activeSheetIdx.value;
  templateSheets.value[idx] = JSON.parse(
    JSON.stringify(templateSheetsCache.value[idx])
  );
  // 重置后已确定状态失效
  onlineEditConfirmed.value = false;
  parseSuccess.value = false;
  ElMessage.success(`已重置 ${templateSheets.value[idx].name} 页`);
  nextTick(() => animateSheetRowsIn());
};

// 重置全部（不请求后端，从缓存恢复全部 sheet）
const resetAllSheets = () => {
  if (!templateSheetsCache.value.length) return;
  templateSheets.value = JSON.parse(JSON.stringify(templateSheetsCache.value));
  onlineEditConfirmed.value = false;
  parseSuccess.value = false;
  ElMessage.success("已重置全部规则为默认");
  nextTick(() => {
    moveSheetIndicator(false);
    animateSheetRowsIn();
  });
};

// 切换左侧 Sheet
const switchSheet = (idx: number) => {
  if (idx === activeSheetIdx.value) return;
  const dir = idx > activeSheetIdx.value ? 1 : -1;
  activeSheetIdx.value = idx;
  moveSheetIndicator(true);
  // 右侧表格做翻页效果
  nextTick(() => {
    if (sheetTableRef.value) {
      gsap.fromTo(
        sheetTableRef.value,
        { x: dir * 36, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.45, ease: "power3.out" }
      );
    }
    animateSheetRowsIn();
  });
};

// 左侧高亮指示条移动
const moveSheetIndicator = (animate: boolean) => {
  const el = sheetItemEls.value[activeSheetIdx.value];
  if (!el || !sheetListRef.value) return;
  const top = el.offsetTop;
  const height = el.offsetHeight;
  if (animate) {
    gsap.to(sheetIndicatorStyle.value, {
      onUpdate: () => {
        sheetIndicatorStyle.value = { ...sheetIndicatorStyle.value };
      },
      duration: 0.4,
      ease: "power3.out",
    });
  }
  sheetIndicatorStyle.value = {
    transform: `translateY(${top}px)`,
    height: `${height}px`,
    opacity: "1",
  };
};

// 表格行依次入场
const animateSheetRowsIn = () => {
  if (!sheetTableRef.value) return;
  const rows = sheetTableRef.value.querySelectorAll("tbody tr");
  if (!rows.length) return;
  gsap.fromTo(
    rows,
    { y: 12, opacity: 0 },
    {
      y: 0,
      opacity: 1,
      duration: 0.35,
      stagger: 0.025,
      ease: "power2.out",
    }
  );
};

// Tab Pane 入场
const animatePane = (el: HTMLElement | undefined, from: "left" | "right") => {
  if (!el) return;
  gsap.fromTo(
    el,
    { x: from === "right" ? 30 : -30, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.4, ease: "power3.out" }
  );
};

// 在线编辑「确定」按钮
const confirmOnlineEdit = () => {
  if (!templateSheets.value.length) {
    ElMessage.warning("模板未加载");
    return;
  }
  onlineEditConfirmed.value = true;
  // 已确定，但解析需要单独点击
  parseSuccess.value = false;
  dynamicParsedData.value = null;
  dynamicPreviewVisible.value = false;
  ElMessage.success("在线编辑已确定，请点击「解析协议」");
};

// 解析协议按钮（统一入口）
const parseProtocol = async () => {
  if (!canParse.value) return;
  if (!extractedShipId.value) {
    ElMessage.error("无法获取船舶ID（请先上传静态协议）");
    return;
  }
  parseLoading.value = true;
  parseSuccess.value = false;
  dynamicParsedData.value = null;
  dynamicPreviewVisible.value = false;
  try {
    let parsed: Record<string, any> | null = null;
    if (templateTab.value === "export") {
      // 走 xlsx 解析
      parsed = await parseCcsFileApi({
        filePath: dynamicFilePath.value,
        protocol: "dynamic",
        shipId: extractedShipId.value,
        version: VERSION,
        author: AUTHOR,
      });
    } else {
      // 走在线规则解析
      parsed = await parseDynamicOnlineApi({
        sheets: templateSheets.value,
        shipId: extractedShipId.value,
        version: VERSION,
        author: AUTHOR,
      });
    }
    if (parsed) {
      dynamicParsedData.value = parsed;
      parseSuccess.value = true;
      ElMessage.success("协议解析成功，可发送报文");
      await nextTick();
      animateStepDone(templateStepRef.value);
    } else {
      ElMessage.error("协议解析失败");
    }
  } catch (e) {
    ElMessage.error("协议解析失败");
  } finally {
    parseLoading.value = false;
  }
};

const toggleDynamicPreview = async () => {
  dynamicPreviewVisible.value = !dynamicPreviewVisible.value;
  await nextTick();
  if (dynamicPreviewVisible.value) {
    animatePreview(dynamicPreviewRef.value);
    updateLineNumbers(dynamicPreviewRef.value);
  }
};

// ======================== 全局发送 ========================
const sending = ref(false);
const isWaitingVisible = ref(false);

const sendReport = async () => {
  if (!parseSuccess.value || !dynamicParsedData.value) return;
  sending.value = true;
  isWaitingVisible.value = true;
  try {
    const success = await sendCcsReportApi({
      packageData: dynamicParsedData.value,
      shipId: extractedShipId.value,
    });
    isWaitingVisible.value = false;
    if (success) {
      sendSuccess.value = true;
      ElMessage.success("报文发送成功");
      await nextTick();
      animateStepDone(dynamicStepRef.value);
    } else {
      ElMessage.error("报文发送失败");
    }
  } finally {
    sending.value = false;
  }
};

// ======================== JSON 格式化与语法高亮 ========================
/**
 * 自定义 JSON 格式化：对象缩进，数组单行显示（逗号后加空格）
 */
function formatJson(data: any, indent = 0): string {
  if (data === null) return "null";
  if (typeof data !== "object") return JSON.stringify(data);

  const pad = "  ".repeat(indent);
  const padInner = "  ".repeat(indent + 1);

  if (Array.isArray(data)) {
    if (data.length === 0) return "[]";
    const items = data.map((item) => {
      if (item === null) return "null";
      if (typeof item === "object") return formatJson(item, indent + 1);
      return highlightToken(JSON.stringify(item));
    });
    return `[${items.join(", ")}]`;
  }

  const entries = Object.entries(data as Record<string, any>);
  if (entries.length === 0) return "{}";
  const lines = entries.map(([key, val]) => {
    let formattedVal: string;
    if (val === null) {
      formattedVal = '<span class="json-null">null</span>';
    } else if (Array.isArray(val)) {
      formattedVal = formatJson(val, indent + 1);
    } else if (typeof val === "object") {
      formattedVal = formatJson(val, indent + 1);
    } else {
      formattedVal = highlightToken(JSON.stringify(val));
    }
    const keySpan = `<span class="json-key">"${escapeHtml(key)}"</span>`;
    return `${padInner}${keySpan}: ${formattedVal}`;
  });
  return `{\n${lines.join(",\n")}\n${pad}}`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function highlightToken(raw: string): string {
  if (raw === undefined) return "";
  if (raw === "null") return '<span class="json-null">null</span>';
  if (raw === "true" || raw === "false")
    return `<span class="json-boolean">${raw}</span>`;
  if (/^-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?$/.test(raw))
    return `<span class="json-number">${raw}</span>`;
  return `<span class="json-string">${raw}</span>`;
}

function highlightJson(data: Record<string, any> | null): string {
  if (!data) return "";
  return formatJson(data, 0);
}

function jsonLineCount(data: Record<string, any> | null): number {
  if (!data) return 0;
  return JSON.stringify(data, null, 2).split("\n").length;
}

function copyJson(data: Record<string, any> | null) {
  if (!data) return;
  // 复制纯文本格式（带数组单行），strip HTML 标签
  const tmp = document.createElement("div");
  tmp.innerHTML = highlightJson(data);
  const text = tmp.textContent || tmp.innerText || "";
  navigator.clipboard.writeText(text).then(() => {
    ElMessage.success("已复制到剪贴板");
  });
}

function updateLineNumbers(container: HTMLElement | undefined) {
  if (!container) return;
  const codeEl = container.querySelector(".json-code");
  const lineNumEl = container.querySelector(".json-line-numbers");
  if (!codeEl || !lineNumEl) return;
  const lines = (codeEl.textContent || "").split("\n").length;
  lineNumEl.innerHTML = Array.from(
    { length: lines },
    (_, i) => `<span>${i + 1}</span>`
  ).join("");
}

function animatePreview(el: HTMLElement | undefined) {
  if (!el) return;
  gsap.fromTo(
    el,
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" }
  );
}

function animateStepDone(el: HTMLElement | undefined) {
  if (!el) return;
  gsap.to(el, {
    borderColor: "rgba(103, 194, 58, 0.5)",
    boxShadow:
      "0 0 0 1px rgba(103, 194, 58, 0.3), 0 4px 20px rgba(103, 194, 58, 0.1)",
    duration: 0.5,
    ease: "power2.out",
    yoyo: true,
    repeat: 1,
    onComplete: () => {
      gsap.set(el, {
        borderColor: "",
        boxShadow: "",
      });
    },
  });
}

// ======================== 入场动画 ========================
function playEntrance() {
  const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

  if (titleRef.value) {
    tl.fromTo(
      titleRef.value,
      { opacity: 0, y: -20 },
      { opacity: 1, y: 0, duration: 0.4 }
    );
  }
  if (infoBarRef.value) {
    tl.fromTo(
      infoBarRef.value,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.35 },
      "-=0.15"
    );
  }
  if (stepperRef.value) {
    tl.fromTo(
      stepperRef.value,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.4 },
      "-=0.15"
    );
  }

  const steps = [
    staticStepRef.value,
    templateStepRef.value,
    dynamicStepRef.value,
  ].filter(Boolean);
  gsap.set(steps, { opacity: 0, y: 30 });
  tl.to(
    steps,
    { opacity: 1, y: 0, duration: 0.45, stagger: 0.12, ease: "power3.out" },
    "-=0.1"
  );

  const allCards = pageRef.value?.querySelectorAll(".anim-card");
  allCards?.forEach((card) => {
    const el = card as HTMLElement;
    el.addEventListener("mouseenter", () => {
      gsap.to(el, {
        y: -2,
        boxShadow: "0 6px 24px rgba(0,0,0,0.07)",
        duration: 0.2,
        ease: "power2.out",
      });
    });
    el.addEventListener("mouseleave", () => {
      gsap.to(el, {
        y: 0,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        duration: 0.2,
        ease: "power2.out",
      });
    });
  });
}

watch(staticParsedData, async () => {
  await nextTick();
  updateLineNumbers(staticPreviewRef.value);
});
watch(dynamicParsedData, async () => {
  await nextTick();
  updateLineNumbers(dynamicPreviewRef.value);
});

onMounted(() => {
  nextTick(() => playEntrance());
});
</script>

<style lang="less" scoped>
.ccs-report {
  padding: 7px;
  overflow: auto;
  max-width: 960px;
  margin: 0 auto;
}

/* ======================== 页面头部 ======================== */
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 20px;
  h1 {
    font-family: "PingFang SC", "SimHei", sans-serif;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 2px;
    background: linear-gradient(135deg, #409eff 0%, #7c5ce7 50%, #e040fb 100%);
    background-size: 200% 200%;
    animation: gradient-shift 4s ease infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
  }
}
@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
.icon-arrow_a {
  font-size: 24px;
  cursor: pointer;
  color: #409eff;
  transition: transform 0.3s ease;
  &:hover {
    transform: translateX(-3px);
  }
}

/* ======================== 基础信息栏 ======================== */
.info-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 14px 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.info-label {
  font-size: 13px;
  color: #909399;
}
.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  &.locked {
    color: #606266;
    padding: 2px 10px;
    background: #f5f7fa;
    border-radius: 6px;
    font-size: 13px;
  }
}
.info-divider {
  width: 1px;
  height: 20px;
  background: #e4e7ed;
}

/* ======================== 步骤流程指示器 ======================== */
.stepper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 80px;
  padding: 0 40px 28px;
}
.step-line {
  position: absolute;
  top: 15px;
  left: calc(50% - 140px);
  right: calc(50% - 140px);
  height: 2px;
  background: #e4e7ed;
  border-radius: 1px;
  &::after {
    content: "";
    display: block;
    width: 0%;
    height: 100%;
    background: linear-gradient(90deg, #409eff, #7c5ce7);
    border-radius: 1px;
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &.active::after {
    width: 100%;
  }
}
.step-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1;
}
.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  background: #fff;
  color: #c0c4cc;
  border: 2px solid #dcdfe6;
  transition: all 0.4s ease;
}
.step-node.done .step-circle {
  background: linear-gradient(135deg, #409eff, #7c5ce7);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 2px 10px rgba(64, 158, 255, 0.35);
}
.step-node.current .step-circle {
  border-color: #409eff;
  color: #409eff;
  animation: pulse-ring 2s ease infinite;
}
@keyframes pulse-ring {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(64, 158, 255, 0);
  }
}
.step-label {
  font-size: 13px;
  font-weight: 600;
  color: #c0c4cc;
  transition: color 0.3s ease;
}
.step-node.done .step-label {
  color: #409eff;
}
.step-node.current .step-label {
  color: #303133;
}

/* ======================== 步骤卡片 ======================== */
.step-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 2px solid transparent;
  margin-bottom: 16px;
  transition: border-color 0.3s ease;
  &.disabled {
    opacity: 0.55;
    pointer-events: none;
  }
}
.step-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.step-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.step-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.step-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.dot-pending {
  background: #e6a23c;
  box-shadow: 0 0 6px rgba(230, 162, 60, 0.4);
}
.dot-success {
  background: #67c23a;
  box-shadow: 0 0 6px rgba(103, 194, 58, 0.4);
}
.dot-disabled {
  background: #c0c4cc;
}
.step-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #303133;
  font-family: "PingFang SC", "SimHei", sans-serif;
}
.step-card-desc {
  font-size: 13px;
  color: #909399;
}
.lock-tag {
  font-size: 11px;
}
.file-tag {
  font-size: 12px;
  color: #606266;
  background: #f0f2f5;
  padding: 3px 10px;
  border-radius: 6px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.status-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
}
.tag-idle {
  background: #f4f4f5;
  color: #909399;
}
.tag-success {
  background: #e1f3d8;
  color: #67c23a;
}

/* ======================== 上传区域 ======================== */
.ccs-upload {
  margin-bottom: 16px;
  :deep(.el-upload) {
    width: 100%;
  }
  :deep(.el-upload-dragger) {
    width: 100%;
    min-height: 100px;
    border: 2px dashed #dcdfe6;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafbfc;
    transition: all 0.3s ease;
    &:hover {
      border-color: #409eff;
      background: #ecf5ff;
    }
  }
  :deep(.el-upload__text) {
    display: none;
  }
}
.upload-area {
  text-align: center;
}
.upload-icon-wrap {
  color: #c0c4cc;
  margin-bottom: 6px;
}
.upload-text {
  font-size: 14px;
  color: #606266;
  margin: 0 0 4px;
}
.upload-hint {
  font-size: 12px;
  color: #c0c4cc;
  margin: 0;
}
.step-actions {
  display: flex;
  gap: 12px;
}

/* ======================== Step2 模板下载提示 ======================== */
.template-tip {
  margin: 20px 0;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fff7e6 0%, #fffbf2 100%);
  border-left: 4px solid #faad14;
  border-radius: 6px;
}
.template-tip p {
  margin: 0;
  color: #614700;
  font-size: 14px;
  line-height: 1.7;
}
.template-tip-small {
  margin-top: 8px !important;
  font-size: 12px !important;
  color: #8c6a00 !important;
}

/* ======================== Step2 Tabs ======================== */
.template-tabs {
  margin-top: 12px;
}
.template-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
}
.template-tabs :deep(.el-tabs__nav-wrap::after) {
  height: 1px;
}
.tab-pane-inner {
  padding: 16px 0 0;
}

/* fancy-tabs：居中 + 卡片式 + 渐变高亮 */
.fancy-tabs :deep(.el-tabs__header) {
  margin-bottom: 18px;
}
.fancy-tabs :deep(.el-tabs__nav-wrap) {
  display: flex;
  justify-content: center;
}
.fancy-tabs :deep(.el-tabs__nav-wrap::after) {
  display: none;
}
.fancy-tabs :deep(.el-tabs__nav) {
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  padding: 4px;
  background: var(--el-fill-color-lighter);
  display: inline-flex;
  gap: 4px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 2px 8px rgba(0, 0, 0, 0.04);
}
.fancy-tabs :deep(.el-tabs__item) {
  height: 38px;
  line-height: 38px;
  border: none !important;
  border-radius: 8px;
  padding: 0 22px;
  font-size: 14px;
  color: var(--el-text-color-regular);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.fancy-tabs :deep(.el-tabs__item:hover) {
  color: var(--el-color-warning-dark-2);
  background: rgba(250, 173, 20, 0.08);
}
.fancy-tabs :deep(.el-tabs__item.is-active) {
  color: #fff !important;
  background: linear-gradient(135deg, #faad14 0%, #f7891a 100%);
  box-shadow: 0 4px 14px rgba(250, 173, 20, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  font-weight: 600;
}
.fancy-tabs :deep(.el-tabs__active-bar) {
  display: none;
}
.fancy-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.fancy-tab-label .el-icon {
  font-size: 15px;
}

/* 导出 tab 内的按钮区 */
.export-actions {
  margin: 0 0 16px;
}
.upload-success-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--el-color-success);
}

/* ======================== 在线编辑 ======================== */
.online-editor-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px 0;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}
.online-editor {
  display: flex;
  gap: 20px;
  min-height: 320px;
}

/* 左侧 Sheet 列表 */
.sheet-list {
  position: relative;
  flex-shrink: 0;
  width: max-content;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  user-select: none;
}
.sheet-list-indicator {
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--el-color-warning), #f7ba2a);
  border-radius: 0 3px 3px 0;
  box-shadow: 0 0 8px rgba(250, 173, 20, 0.5);
  transition: none;
  pointer-events: none;
  z-index: 1;
}
.sheet-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
}
.sheet-item:hover {
  background: var(--el-fill-color-light);
}
.sheet-item.active {
  background: var(--el-fill-color);
  color: var(--el-color-warning-dark-2);
}
.sheet-index {
  font-size: 11px;
  color: var(--el-text-color-placeholder);
  font-variant-numeric: tabular-nums;
  min-width: 18px;
}
.sheet-item.active .sheet-index {
  color: var(--el-color-warning);
}
.sheet-name {
  font-size: 13px;
  white-space: nowrap;
}
.sheet-rows {
  margin-left: auto;
  font-size: 10px;
  color: var(--el-text-color-placeholder);
  background: var(--el-fill-color);
  padding: 1px 6px;
  border-radius: 8px;
  white-space: nowrap;
}

/* 右侧表格面板 */
.sheet-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sheet-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}
.sheet-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sheet-panel-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}
.sheet-panel-tip {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}
.sheet-panel-tip .tip-key {
  color: var(--el-color-warning);
  font-weight: 600;
}

/* 表格 */
.sheet-table-wrap {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
  background: var(--el-bg-color);
}
.sheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sheet-table th,
.sheet-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
  text-align: center;
  white-space: nowrap;
}
.sheet-table thead th {
  background: var(--el-fill-color-lighter);
  font-weight: 600;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  position: sticky;
  top: 0;
  z-index: 1;
}
.sheet-table .code-row td {
  background: #fef9e7;
  font-weight: 600;
  font-size: 12px;
}
.sheet-table .code-cell {
  color: var(--el-color-warning-dark-2);
  letter-spacing: 1px;
}
.sheet-table .locked {
  color: var(--el-text-color-regular);
  background: var(--el-fill-color-lighter);
}
.sheet-table .row-key {
  font-family: "SF Mono", "Menlo", monospace;
  font-size: 12px;
}
.sheet-table .data-row {
  transition: background 0.15s;
}
.sheet-table .data-row:hover {
  background: var(--el-color-warning-light-9);
}
.sheet-table .editable-cell {
  padding: 4px 6px;
}
.cell-input :deep(.el-input__wrapper) {
  border-radius: 4px;
  box-shadow: 0 0 0 1px var(--el-border-color) inset;
  transition: box-shadow 0.2s;
}
.cell-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px var(--el-color-warning) inset;
}
.cell-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px var(--el-color-warning) inset,
    0 0 6px rgba(250, 173, 20, 0.25);
}

.edit-actions {
  margin-top: 16px;
  justify-content: flex-end;
}

/* ======================== Step2 解析栏 ======================== */
.parse-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f6f9ff 0%, #fafbff 100%);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 10px;
}
.parse-bar-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--el-text-color-regular);
}
.parse-bar-status .el-icon {
  font-size: 16px;
}
.parse-bar-status .parse-ok {
  color: var(--el-color-success);
  font-weight: 600;
}
.parse-bar-actions {
  display: flex;
  gap: 10px;
}

/* ======================== Step3 发送区 ======================== */
.send-step-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 22px 24px;
  background: linear-gradient(135deg, #f3fcf6 0%, #fafffd 100%);
  border: 1px solid var(--el-color-success-light-7);
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 16px;
}
.send-step-hint {
  margin: 0;
  font-size: 14px;
  color: var(--el-text-color-regular);
}

/* ======================== JSON 预览 ======================== */
.json-preview-wrap {
  margin-top: 16px;
}
.preview-expand-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.preview-expand-leave-active {
  transition: all 0.3s ease;
}
.preview-expand-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.preview-expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.json-editor {
  border-radius: 10px;
  overflow: hidden;
  background: #1e1e1e;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}
.json-toolbar {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #252526;
  border-bottom: 1px solid #3c3c3c;
  gap: 6px;
}
.json-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  &.red {
    background: #ff5f57;
  }
  &.yellow {
    background: #febc2e;
  }
  &.green {
    background: #28c840;
  }
}
.json-filename {
  flex: 1;
  text-align: center;
  font-size: 12px;
  color: #858585;
  font-family: "Consolas", "Monaco", monospace;
}
.json-meta {
  font-size: 11px;
  color: #555;
  font-family: "Consolas", monospace;
}
.copy-btn {
  font-size: 12px;
  color: #858585 !important;
  &:hover {
    color: #d4d4d4 !important;
  }
}
.json-body {
  display: flex;
  max-height: 500px;
  overflow: auto;
  padding: 16px 0;
}
.json-line-numbers {
  padding: 0 12px 0 16px;
  text-align: right;
  user-select: none;
  color: #555;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.6;
  border-right: 1px solid #3c3c3c;
  min-width: 40px;
  max-width: 48px;
  flex-shrink: 0;
  overflow: hidden;
}
:deep(.json-line-numbers span) {
  display: block;
}
.json-code {
  margin: 0;
  padding: 0 16px;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #d4d4d4;
  white-space: pre;
  flex: 1;
  overflow-x: auto;
}

/* ======================== JSON 语法高亮颜色 ======================== */
:deep(.json-key) {
  color: #9cdcfe;
}
:deep(.json-string) {
  color: #ce9178;
}
:deep(.json-number) {
  color: #b5cea8;
}
:deep(.json-boolean) {
  color: #569cd6;
}
:deep(.json-null) {
  color: #569cd6;
}

/* ======================== 发送栏 ======================== */
.send-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 28px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-top: 8px;
}
.send-bar-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.send-bar-desc {
  font-size: 13px;
  color: #909399;
}
.send-progress {
  display: flex;
  gap: 4px;
}
.progress-segment {
  width: 80px;
  height: 4px;
  border-radius: 2px;
  background: #e4e7ed;
  transition: background 0.4s ease;
  &.filled {
    background: linear-gradient(90deg, #409eff, #7c5ce7);
  }
}

/* ======================== 等待对话框 ======================== */
.waiting-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
}
.waiting-ring {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid #e4e7ed;
  border-top-color: #409eff;
  animation: ring-spin 1s linear infinite;
  margin-bottom: 16px;
}
@keyframes ring-spin {
  to {
    transform: rotate(360deg);
  }
}
.waiting-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}
.waiting-subtitle {
  font-size: 13px;
  color: #909399;
}

/* ======================== 滚动条 ======================== */
.json-body::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.json-body::-webkit-scrollbar-track {
  background: #1e1e1e;
}
.json-body::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 3px;
}
.json-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
